import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Plus, Trash2, Wine, Package, MapPin, Check } from "lucide-react";
import { toast } from "sonner";
import { DIETS, dietLabel } from "../../lib/diets";

// ---------------- WINES ----------------
const EMPTY_WINE = {
  name: "", winery: "", vintage: 2022, region: "", country: "Italia",
  grape: "", color: "Rosso", denomination: "DOC", alcohol: 13.5,
  price_per_bottle: 25, price_per_carton: 280, price_per_pallet: 1900,
  bottles_per_carton: 6, cartons_per_pallet: 60, suggested_margin: 35,
  stock_available: 1000, image: "", description: "",
};

function WinesTab() {
  const [wines, setWines] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_WINE);

  const reload = () => api.get("/admin/wines").then((r) => setWines(r.data));
  useEffect(() => { reload(); }, []);

  const submit = async () => {
    try {
      await api.post("/admin/wines", { ...form,
        vintage: Number(form.vintage), alcohol: Number(form.alcohol),
        price_per_bottle: Number(form.price_per_bottle),
        price_per_carton: Number(form.price_per_carton),
        price_per_pallet: Number(form.price_per_pallet),
        stock_available: Number(form.stock_available),
        suggested_margin: Number(form.suggested_margin),
      });
      toast.success("Vino creato");
      setCreating(false); setForm(EMPTY_WINE); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Eliminare "${name}"?`)) return;
    await api.delete(`/admin/wines/${id}`);
    toast.success("Eliminato"); reload();
  };

  return (
    <div className="space-y-4">
      {!creating ? (
        <Button data-testid="add-wine-btn" onClick={() => setCreating(true)} className="bg-[hsl(var(--primary))] gap-2"><Plus className="h-4 w-4" />Nuovo vino</Button>
      ) : (
        <div data-testid="wine-form" className="bg-card border border-border rounded-sm p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            ["name", "Nome vino *"], ["winery", "Cantina *"], ["vintage", "Annata *", "number"],
            ["region", "Regione *"], ["country", "Paese"], ["grape", "Vitigno *"],
            ["denomination", "Denominazione (DOC/DOCG/AOC)"], ["alcohol", "Alcol %", "number"],
            ["price_per_bottle", "€/bottiglia *", "number"], ["price_per_carton", "€/cartone", "number"],
            ["price_per_pallet", "€/pallet", "number"], ["bottles_per_carton", "btl/cartone", "number"],
            ["cartons_per_pallet", "cart/pallet", "number"], ["stock_available", "Stock *", "number"],
            ["suggested_margin", "Margine %", "number"], ["image", "URL Immagine"],
          ].map(([k, lbl, type]) => (
            <div key={k}>
              <Label className="text-xs">{lbl}</Label>
              <Input data-testid={`wine-${k}`} type={type || "text"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
            </div>
          ))}
          <div className="md:col-span-3">
            <Label className="text-xs">Tipologia</Label>
            <Select value={form.color} onValueChange={(v) => setForm({ ...form, color: v })}>
              <SelectTrigger data-testid="wine-color"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Rosso", "Bianco", "Rosato", "Spumante"].map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3">
            <Label className="text-xs">Descrizione</Label>
            <Textarea data-testid="wine-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
          </div>
          <div className="md:col-span-3 flex gap-2">
            <Button data-testid="wine-submit" onClick={submit} className="bg-[hsl(var(--primary))]">Salva vino</Button>
            <Button variant="outline" onClick={() => { setCreating(false); setForm(EMPTY_WINE); }}>Annulla</Button>
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-sm overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Vino</TableHead><TableHead>Annata</TableHead><TableHead>Regione</TableHead><TableHead>€/btl</TableHead><TableHead>Stock</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {wines.map((w) => (
              <TableRow key={w.id} data-testid={`admin-wine-${w.id}`}>
                <TableCell className="font-medium">{w.name}</TableCell>
                <TableCell>{w.vintage}</TableCell>
                <TableCell>{w.region}</TableCell>
                <TableCell className="font-bold">€{Number(w.price_per_bottle).toFixed(2)}</TableCell>
                <TableCell>{Number(w.stock_available || 0).toLocaleString()}</TableCell>
                <TableCell><button data-testid={`del-wine-${w.id}`} onClick={() => del(w.id, w.name)} className="text-destructive hover:opacity-70"><Trash2 className="h-4 w-4" /></button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ---------------- BOXES ----------------
const EMPTY_BOX = {
  name: "", slug: "", category: "regional", region: "Toscana",
  season: "estate", price: 39.9, serves: 4,
  image: "", description: "", contents: [], diets: [],
};

function DietPicker({ value, onChange }) {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (v) => {
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  };
  return (
    <div data-testid="box-diets-picker" className="flex flex-wrap gap-2">
      {DIETS.map((d) => {
        const on = selected.includes(d.value);
        return (
          <button
            key={d.value}
            type="button"
            data-testid={`box-diet-${d.value}`}
            onClick={() => toggle(d.value)}
            className={`px-3 py-1.5 text-xs rounded-sm border transition-colors flex items-center gap-1.5 ${
              on
                ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]"
                : "bg-white text-[hsl(var(--foreground))] border-border hover:border-[hsl(var(--primary))]"
            }`}
          >
            {on && <Check className="h-3 w-3" />}
            {d.label}
          </button>
        );
      })}
    </div>
  );
}

function BoxesTab() {
  const [boxes, setBoxes] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_BOX);

  const reload = () => api.get("/admin/boxes").then((r) => setBoxes(r.data));
  useEffect(() => { reload(); }, []);

  const submit = async () => {
    try {
      await api.post("/admin/boxes", { ...form,
        price: Number(form.price), serves: Number(form.serves),
        slug: form.slug || form.name.toLowerCase().replace(/\s+/g, "-"),
      });
      toast.success("Box creata"); setCreating(false); setForm(EMPTY_BOX); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Eliminare "${name}"?`)) return;
    await api.delete(`/admin/boxes/${id}`);
    toast.success("Eliminata"); reload();
  };

  return (
    <div className="space-y-4">
      {!creating ? (
        <Button data-testid="add-box-btn" onClick={() => setCreating(true)} className="bg-[hsl(var(--primary))] gap-2"><Plus className="h-4 w-4" />Nuova box</Button>
      ) : (
        <div data-testid="box-form" className="bg-card border border-border rounded-sm p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          {[["name", "Nome *"], ["slug", "Slug"], ["region", "Regione *"], ["price", "Prezzo €*", "number"], ["serves", "Persone", "number"], ["image", "URL Immagine"]].map(([k, lbl, type]) => (
            <div key={k}>
              <Label className="text-xs">{lbl}</Label>
              <Input data-testid={`box-${k}`} type={type || "text"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
            </div>
          ))}
          <div>
            <Label className="text-xs">Categoria *</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="box-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[["recipe", "Per Ricetta"], ["experience", "Esperienza"], ["regional", "Regionale"], ["seasonal", "Stagionale"], ["gift", "Regalo"]].map(([k, l]) => (<SelectItem key={k} value={k}>{l}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Stagione</Label>
            <Select value={form.season} onValueChange={(v) => setForm({ ...form, season: v })}>
              <SelectTrigger data-testid="box-season"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["primavera", "estate", "autunno", "inverno"].map((s) => (<SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3">
            <Label className="text-xs">Diete supportate <span className="text-muted-foreground font-normal">(seleziona una o più)</span></Label>
            <div className="mt-2">
              <DietPicker value={form.diets || []} onChange={(v) => setForm({ ...form, diets: v })} />
            </div>
          </div>
          <div className="md:col-span-3">
            <Label className="text-xs">Descrizione</Label>
            <Textarea data-testid="box-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
          </div>
          <div className="md:col-span-3 flex gap-2">
            <Button data-testid="box-submit" onClick={submit} className="bg-[hsl(var(--primary))]">Salva box</Button>
            <Button variant="outline" onClick={() => { setCreating(false); setForm(EMPTY_BOX); }}>Annulla</Button>
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-sm overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Box</TableHead><TableHead>Categoria</TableHead><TableHead>Regione</TableHead><TableHead>Diete</TableHead><TableHead>Prezzo</TableHead><TableHead>Persone</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {boxes.map((b) => (
              <TableRow key={b.id} data-testid={`admin-box-${b.id}`}>
                <TableCell className="font-medium">{b.name}</TableCell>
                <TableCell><span className="chip bg-[hsl(var(--muted))]">{b.category}</span></TableCell>
                <TableCell>{b.region || "—"}</TableCell>
                <TableCell>
                  {(b.diets || []).length === 0 ? (
                    <span className="text-xs text-muted-foreground">—</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {b.diets.map((d) => (
                        <span key={d} data-testid={`box-${b.id}-diet-${d}`} className="text-[10px] px-1.5 py-0.5 bg-[hsl(var(--muted))] rounded-sm">{dietLabel(d)}</span>
                      ))}
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-bold text-[hsl(var(--primary))]">€{Number(b.price).toFixed(2)}</TableCell>
                <TableCell>{b.serves}</TableCell>
                <TableCell><button data-testid={`del-box-${b.id}`} onClick={() => del(b.id, b.name)} className="text-destructive hover:opacity-70"><Trash2 className="h-4 w-4" /></button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ---------------- EXPERIENCES ----------------
const EMPTY_EXP = {
  name: "", type: "cantina_visit", region: "Toscana", winery: "",
  price: 80, guests_max: 4, duration: "2 ore",
  image: "", description: "",
};

function ExperiencesTab() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_EXP);

  const reload = () => api.get("/admin/experiences").then((r) => setItems(r.data));
  useEffect(() => { reload(); }, []);

  const submit = async () => {
    try {
      await api.post("/admin/experiences", { ...form,
        price: Number(form.price), guests_max: Number(form.guests_max),
      });
      toast.success("Esperienza creata"); setCreating(false); setForm(EMPTY_EXP); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Eliminare "${name}"?`)) return;
    await api.delete(`/admin/experiences/${id}`);
    toast.success("Eliminata"); reload();
  };

  return (
    <div className="space-y-4">
      {!creating ? (
        <Button data-testid="add-exp-btn" onClick={() => setCreating(true)} className="bg-[hsl(var(--primary))] gap-2"><Plus className="h-4 w-4" />Nuova esperienza</Button>
      ) : (
        <div data-testid="exp-form" className="bg-card border border-border rounded-sm p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          {[["name", "Nome esperienza *"], ["winery", "Cantina/Resort *"], ["region", "Regione *"], ["price", "Prezzo €/persona *", "number"], ["guests_max", "Ospiti max", "number"], ["duration", "Durata (es. 2 ore)"]].map(([k, lbl, type]) => (
            <div key={k}>
              <Label className="text-xs">{lbl}</Label>
              <Input data-testid={`exp-${k}`} type={type || "text"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
            </div>
          ))}
          <div>
            <Label className="text-xs">Tipo *</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="exp-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[["cantina_visit", "Visita in Cantina"], ["resort", "Wine Resort"], ["cooking_class", "Cooking Class"], ["truffle_hunting", "Truffle Hunting"], ["oil_tasting", "Olive Oil Tasting"], ["harvest", "Vendemmia"]].map(([k, l]) => (<SelectItem key={k} value={k}>{l}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">URL Immagine</Label>
            <Input data-testid="exp-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://… o /api/static/…" />
          </div>
          <div className="md:col-span-3">
            <Label className="text-xs">Descrizione</Label>
            <Textarea data-testid="exp-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} />
          </div>
          <div className="md:col-span-3 flex gap-2">
            <Button data-testid="exp-submit" onClick={submit} className="bg-[hsl(var(--primary))]">Salva esperienza</Button>
            <Button variant="outline" onClick={() => { setCreating(false); setForm(EMPTY_EXP); }}>Annulla</Button>
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-sm overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Esperienza</TableHead><TableHead>Tipo</TableHead><TableHead>Regione</TableHead><TableHead>€/pers</TableHead><TableHead>Max</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((e) => (
              <TableRow key={e.id} data-testid={`admin-exp-${e.id}`}>
                <TableCell className="font-medium">{e.name}<div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" />{e.winery}</div></TableCell>
                <TableCell><span className="chip bg-[hsl(var(--muted))]">{e.type}</span></TableCell>
                <TableCell>{e.region}</TableCell>
                <TableCell className="font-bold text-[hsl(var(--primary))]">€{Number(e.price).toFixed(0)}</TableCell>
                <TableCell>{e.guests_max}</TableCell>
                <TableCell><button data-testid={`del-exp-${e.id}`} onClick={() => del(e.id, e.name)} className="text-destructive hover:opacity-70"><Trash2 className="h-4 w-4" /></button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function AdminCatalog() {
  return (
    <div data-testid="admin-catalog-page">
      <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent))] font-semibold mb-2">Super Admin · Catalogo</div>
      <h1 className="editorial-heading text-4xl md:text-5xl">Gestione Catalogo</h1>
      <p className="text-muted-foreground mt-2 mb-6">Crea e gestisci vini, food box ed esperienze.</p>
      <Tabs defaultValue="wines">
        <TabsList>
          <TabsTrigger data-testid="cat-tab-wines" value="wines" className="gap-2"><Wine className="h-4 w-4" />Vini</TabsTrigger>
          <TabsTrigger data-testid="cat-tab-boxes" value="boxes" className="gap-2"><Package className="h-4 w-4" />Box</TabsTrigger>
          <TabsTrigger data-testid="cat-tab-experiences" value="experiences" className="gap-2"><MapPin className="h-4 w-4" />Esperienze</TabsTrigger>
        </TabsList>
        <TabsContent value="wines" className="mt-6"><WinesTab /></TabsContent>
        <TabsContent value="boxes" className="mt-6"><BoxesTab /></TabsContent>
        <TabsContent value="experiences" className="mt-6"><ExperiencesTab /></TabsContent>
      </Tabs>
    </div>
  );
}
